import { io, Socket } from 'socket.io-client';
import logger from './logger';
import config from '../config';
import type { 
  ServerToClientEvents, 
  ClientToServerEvents 
} from '../types';

class GameSocket {
  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
  private listeners: Map<string, Set<Function>> = new Map();

  connect(token?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Если уже подключены
      if (this.socket?.connected) {
        resolve();
        return;
      }

      // Если есть старый сокет, отключаем
      if (this.socket) {
        this.disconnect();
      }

      this.socket = io(config.api.wsUrl, {
        transports: ['websocket'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        withCredentials: true // Важно для отправки cookies
      });

      // Регистрируем все обработчики событий
      const events: (keyof ServerToClientEvents)[] = [
        'auth.success', 'auth.error', 'character.data', 'character.created',
        'character.error', 'character-classes.data', 'error',
        'battle-request.list-update', 'battle-request.my-active-result',
        'battle-request.created', 'battle-request.cancelled',
        'battle-request.error', 'battle-request.new', 'battle-request.removed',
        'battle.started', 'battle.auto-result', 'character.update', 'notification', 
        'locations.data', 'location.connections', 'location.travel-started',
        'location.travel-completed', 'location.travel-error', 'disconnect'
      ];

      events.forEach(event => {
        this.socket!.on(event as any, (data: any) => {
          this.notifyListeners(event, data);
        });
      });

      this.socket.on('connect', () => {
        logger.info('Socket', 'Connected to game server');
        // Автоматически логинимся при подключении
        // Если токен не передан или это специальный токен для cookie-auth, 
        // отправляем 'cookie-auth' чтобы сервер проверил cookie
        const authToken = token || 'cookie-auth';
        this.emit('auth.login', { token: authToken });
      });

      // Ждем успешной авторизации
      const authSuccessHandler = () => {
        logger.info('Socket', 'Authentication successful');
        this.off('auth.success', authSuccessHandler);
        this.off('auth.error', authErrorHandler);
        resolve();
      };

      const authErrorHandler = (data: { error: string }) => {
        logger.error('Socket', 'Authentication failed:', data.error);
        this.off('auth.success', authSuccessHandler);
        this.off('auth.error', authErrorHandler);
        reject(new Error(data.error));
      };

      this.on('auth.success', authSuccessHandler);
      this.on('auth.error', authErrorHandler);

      // Таймаут подключения
      setTimeout(() => {
        if (!this.socket?.connected) {
          this.off('auth.success', authSuccessHandler);
          this.off('auth.error', authErrorHandler);
          this.disconnect();
          reject(new Error('Connection timeout'));
        }
      }, 5000);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  emit(event: keyof ClientToServerEvents, data?: any) {
    if (!this.socket?.connected) {
      logger.error('Socket', `Socket not connected for event: ${event}`);
      return;
    }
    this.socket.emit(event, data);
  }

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Возвращаем функцию для отписки
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  off(event: string, callback?: Function) {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
    }
  }

  private notifyListeners(event: string, data: any) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  logout() {
    this.emit('auth.logout');
    this.disconnect();
  }
}

// Singleton экземпляр
export const gameSocket = new GameSocket();
